import { createFileRoute } from '@tanstack/react-router';
import { Loader2Icon } from 'lucide-react';
import { useState } from 'react';
import { useCheckout, useGetSubscription } from '@/api/subscription/queries';
import { PageContainer } from '@/components/layout/page-container';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { authClient } from '@/lib/auth-client';
import { PricingCard } from './-components/pricing-card';
import { PricingSwitch } from './-components/pricing-switch';
import { PLANS } from './-utils/config';

export const Route = createFileRoute('/_authenticated/_global/pricing/')({
  component: PaymentsComponent,
});

function PaymentsComponent() {
  const [isBillingLoading, setIsBillingLoading] = useState(false);
  const { data: subscription } = useGetSubscription();
  const checkoutMutation = useCheckout();
  const [isYearly, setIsYearly] = useState(false);

  const togglePricingPeriod = () => {
    setIsYearly((state) => !state);
  };

  const handleBuySubscription = (productId: string) => {
    checkoutMutation.mutate({
      products: [productId],
      referenceId: subscription?.activeOrganizationId || undefined,
    });
  };

  const navigateToBilling = async () => {
    setIsBillingLoading(true);

    await authClient.customer.portal();

    setTimeout(() => {
      setIsBillingLoading(false);
    }, 3000);
  };

  return (
    <PageContainer>
      <PageHeader
        title="Pricing Plan"
        rightControls={
          !subscription?.isFree ? (
            <div>
              <Button disabled={isBillingLoading} variant="outline" onClick={navigateToBilling}>
                {isBillingLoading ? (
                  <Loader2Icon className="w-4 h-4 animate-spin" size={16} />
                ) : (
                  'Manage subscription'
                )}
              </Button>
            </div>
          ) : null
        }
      />
      <div className="flex flex-col gap-8">
        <PricingSwitch isYearly={isYearly} onChange={togglePricingPeriod} />
        <section className="flex flex-col justify-center gap-8 sm:flex-row sm:flex-wrap">
          <PricingCard
            {...PLANS.launch}
            disabled={checkoutMutation.isPending}
            isYearly={isYearly}
            onSelect={() => {}}
            isCurrent={subscription?.plan === PLANS.launch.plan}
            controlsActive={!!subscription?.isFree}
          />
          <PricingCard
            {...PLANS.starter}
            disabled={checkoutMutation.isPending}
            isYearly={isYearly}
            onSelect={handleBuySubscription}
            isCurrent={subscription?.plan === PLANS.starter.plan}
            controlsActive={!!subscription?.isFree}
          />
          <PricingCard
            {...PLANS.scaler}
            disabled={checkoutMutation.isPending}
            isYearly={isYearly}
            onSelect={handleBuySubscription}
            isCurrent={subscription?.plan === PLANS.scaler.plan}
            controlsActive={!!subscription?.isFree}
          />
        </section>
        <p className="text-center">
          Need more features?{' '}
          <a
            href="mailto:hey@featurebuddy.com"
            className="dark:text-orange-300 text-orange-700 underline underline-offset-6 decoration-current/30 transition-colors hover:text-orange-600 hover:dark:text-orange-200 hover:decoration-current/40"
          >
            <span className="font-semibold">Contact Us</span>
          </a>{' '}
          for a custom plan.
        </p>
      </div>
    </PageContainer>
  );
}
